import React, { useState, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { login } from '../actions/authActions';
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Heading,
  Text,
  useColorModeValue,
  Container,
  InputGroup,
  InputRightElement,
  IconButton,
  Alert,
  AlertIcon,
  Spinner,
  Flex,
  HStack,
  Divider,
  InputLeftElement,
  ScaleFade,
  Fade,
} from '@chakra-ui/react';
import { ViewIcon, ViewOffIcon } from '@chakra-ui/icons';
import { LockIcon, EmailIcon } from '@chakra-ui/icons';

/**
 * Modern, professional login page component
 * Features:
 * - Responsive design with mobile-first approach
 * - Loading states and error handling
 * - Accessibility improvements
 * - Smooth animations
 * - Professional branding area
 */
const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState({ username: false, password: false });
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { slug } = useParams();

  useEffect(() => {
    if (!slug) {
      setError('Invalid company URL. Please check the address and try again.');
    }
  }, [slug]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Basic validation
    if (!username.trim() || !password.trim()) {
      setError('Please enter both username and password.');
      return;
    }

    setIsLoading(true);
    try {
      const { role, companySlug } = await dispatch(login(username.trim(), password, slug));
      console.log('Login result:', { role, companySlug });
      
      // Small delay for better UX
      await new Promise(resolve => setTimeout(resolve, 300));
      
      if (role === 'super_admin') {
        navigate('/root/manage-company');
      } else {
        navigate(`/${slug}/dashboard`);
      }
    } catch (err) {
      console.error('Login error:', err);
      setError(err.response?.data?.message || 'Login failed. Please check your credentials and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  // Color scheme
  const bgGradient = useColorModeValue(
    'linear(to-br, blue.50, indigo.100, purple.50)',
    'linear(to-br, gray.900, gray.800, gray.900)'
  );
  const boxBgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');
  const focusBorderColor = useColorModeValue('blue.500', 'blue.400');
  const textColor = useColorModeValue('gray.700', 'gray.200');
  const labelColor = useColorModeValue('gray.600', 'gray.400');
  const companyNameColor = useColorModeValue('blue.600', 'blue.400');

  return (
    <Box
      minH="100vh"
      bgGradient={bgGradient}
      display="flex"
      alignItems="center"
      justifyContent="center"
      py={{ base: 8, md: 10 }}
      px={{ base: 4, md: 0 }}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative background elements */}
      <Box
        position="absolute"
        top="-50%"
        right="-20%"
        w="600px"
        h="600px"
        borderRadius="full"
        bgGradient="linear(to-br, blue.200, purple.200)"
        opacity={0.1}
        filter="blur(60px)"
      />
      <Box
        position="absolute"
        bottom="-30%"
        left="-10%"
        w="500px"
        h="500px"
        borderRadius="full"
        bgGradient="linear(to-br, indigo.200, blue.200)"
        opacity={0.1}
        filter="blur(60px)"
      />

      <Container maxW="md" position="relative" zIndex={1}>
        <ScaleFade in={true} initialScale={0.95}>
          <Box
            bg={boxBgColor}
            rounded="2xl"
            boxShadow="2xl"
            p={{ base: 6, md: 10 }}
            borderWidth="1px"
            borderColor={borderColor}
            backdropFilter="blur(10px)"
          >
            <VStack spacing={8} align="stretch">
              {/* Header Section */}
              <VStack spacing={3} align="center">
                <Box
                  w={16}
                  h={16}
                  borderRadius="xl"
                  bgGradient="linear(to-br, blue.500, indigo.600)"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  boxShadow="lg"
                  mb={2}
                >
                  <LockIcon color="white" boxSize={8} />
                </Box>
                <Heading
                  size={{ base: 'lg', md: 'xl' }}
                  textAlign="center"
                  fontWeight="700"
                  color={textColor}
                >
                  Welcome Back
                </Heading>
                <Text
                  fontSize={{ base: 'sm', md: 'md' }}
                  color={labelColor}
                  textAlign="center"
                  maxW="sm"
                >
                  Sign in to{' '}
                  <Text as="span" fontWeight="600" color={companyNameColor}>
                    {slug?.toUpperCase() || 'Your Account'}
                  </Text>
                </Text>
              </VStack>

              {/* Error Alert */}
              {error && (
                <Fade in={!!error}>
                  <Alert
                    status="error"
                    borderRadius="lg"
                    variant="left-accent"
                    fontSize="sm"
                  >
                    <AlertIcon />
                    <Text flex={1}>{error}</Text>
                  </Alert>
                </Fade>
              )}

              {/* Login Form */}
              <form onSubmit={handleSubmit}>
                <VStack spacing={5} align="stretch">
                  {/* Username Field */}
                  <FormControl id="username" isRequired>
                    <FormLabel
                      fontSize="sm"
                      fontWeight="600"
                      color={labelColor}
                      mb={2}
                    >
                      Username
                    </FormLabel>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none" h="full">
                        <EmailIcon color={isFocused.username ? focusBorderColor : labelColor} />
                      </InputLeftElement>
                      <Input
                        type="text"
                        value={username}
                        onChange={(e) => setUsername(e.target.value)}
                        placeholder="Enter your username"
                        onFocus={() => setIsFocused(prev => ({ ...prev, username: true }))}
                        onBlur={() => setIsFocused(prev => ({ ...prev, username: false }))}
                        borderColor={isFocused.username ? focusBorderColor : borderColor}
                        _hover={{ borderColor: focusBorderColor }}
                        _focus={{
                          borderColor: focusBorderColor,
                          boxShadow: `0 0 0 1px ${focusBorderColor}`,
                        }}
                        bg={useColorModeValue('white', 'gray.700')}
                        disabled={isLoading}
                        autoComplete="username"
                      />
                    </InputGroup>
                  </FormControl>

                  {/* Password Field */}
                  <FormControl id="password" isRequired>
                    <FormLabel
                      fontSize="sm"
                      fontWeight="600"
                      color={labelColor}
                      mb={2}
                    >
                      Password
                    </FormLabel>
                    <InputGroup size="lg">
                      <InputLeftElement pointerEvents="none" h="full">
                        <LockIcon color={isFocused.password ? focusBorderColor : labelColor} />
                      </InputLeftElement>
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        placeholder="Enter your password"
                        onFocus={() => setIsFocused(prev => ({ ...prev, password: true }))}
                        onBlur={() => setIsFocused(prev => ({ ...prev, password: false }))}
                        borderColor={isFocused.password ? focusBorderColor : borderColor}
                        _hover={{ borderColor: focusBorderColor }}
                        _focus={{
                          borderColor: focusBorderColor,
                          boxShadow: `0 0 0 1px ${focusBorderColor}`,
                        }}
                        bg={useColorModeValue('white', 'gray.700')}
                        disabled={isLoading}
                        autoComplete="current-password"
                        pr="4.5rem"
                      />
                      <InputRightElement width="4.5rem" h="full">
                        <IconButton
                          h="1.75rem"
                          size="sm"
                          variant="ghost"
                          onClick={() => setShowPassword(!showPassword)}
                          icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                          aria-label={showPassword ? 'Hide password' : 'Show password'}
                          color={labelColor}
                          _hover={{ color: focusBorderColor, bg: 'transparent' }}
                        />
                      </InputRightElement>
                    </InputGroup>
                  </FormControl>

                  {/* Submit Button */}
                  <Button
                    type="submit"
                    colorScheme="blue"
                    size="lg"
                    fontSize="md"
                    fontWeight="600"
                    width="full"
                    height="50px"
                    borderRadius="lg"
                    bgGradient="linear(to-r, blue.500, indigo.600)"
                    _hover={{
                      bgGradient: 'linear(to-r, blue.600, indigo.700)',
                      transform: 'translateY(-1px)',
                      boxShadow: 'lg',
                    }}
                    _active={{
                      transform: 'translateY(0)',
                    }}
                    isLoading={isLoading}
                    loadingText="Signing in..."
                    spinner={<Spinner size="sm" color="white" />}
                    disabled={isLoading || !username.trim() || !password.trim()}
                    transition="all 0.2s"
                    boxShadow="md"
                  >
                    Sign In
                  </Button>
                </VStack>
              </form>

              {/* Footer */}
              <Box pt={2}>
                <Divider mb={4} />
                <Text
                  fontSize="xs"
                  color={labelColor}
                  textAlign="center"
                >
                  Secure login powered by PlayJitto Admin Panel
                </Text>
              </Box>
            </VStack>
          </Box>
        </ScaleFade>
      </Container>
    </Box>
  );
};

export default Login;